import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { t, tr } from "../../i18n/translations";

const BACKEND_TO_URL_SLUG = {
  automaquillaje: "automaquillaje",
  novias: "novias",
  "editorial-course": "editorial",
};

export default function Courses({ onEnroll }) {
  const { lang } = useLang();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => {});
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const formatter = new Intl.DateTimeFormat(
        lang === "es" ? "es-MX" : lang === "jp" ? "ja-JP" : "en-US",
        { day: "2-digit", month: "long", year: "numeric" }
      );
      return formatter.format(d);
    } catch {
      return iso;
    }
  };

  return (
    <section id="courses" className="bg-[#1A1A1A] text-[#FAFAFA] py-28 sm:py-36" data-testid="courses-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 lg:mb-20">
          <div className="lg:col-span-7">
            <p className="overline text-[#8B6D5E] mb-4">{tr(t.courses.overline, lang)}</p>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1]">{tr(t.courses.title, lang)}</h2>
          </div>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {courses.map((c) => (
            <CourseCard key={c.id} c={c} lang={lang} onEnroll={onEnroll} fmtDate={fmtDate} />
          ))}
        </div>

        {/* Mobile carousel */}
        <div
          className="md:hidden flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-6 px-6 pb-6 no-scrollbar"
          data-testid="courses-carousel-mobile"
        >
          {courses.map((c) => (
            <div key={c.id} className="snap-center flex-none w-[85%]" data-testid={`course-slide-${c.slug}`}>
              <CourseCard c={c} lang={lang} onEnroll={onEnroll} fmtDate={fmtDate} />
            </div>
          ))}
        </div>

        {/* Mobile dots */}
        {courses.length > 0 && (
          <div className="md:hidden flex items-center justify-center gap-2 mt-2" aria-hidden>
            {courses.map((_, i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#FAFAFA]/25" />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}

function CourseCard({ c, lang, onEnroll, fmtDate }) {
  const title = c[`title_${lang}`] || c.title_es;
  const desc = c[`description_${lang}`] || c.description_es;
  const urlSlug = BACKEND_TO_URL_SLUG[c.slug] || c.slug;
  return (
    <article data-testid={`course-card-${c.slug}`} className="group flex flex-col h-full">
      <Link to={`/cursos/${urlSlug}`} className="block" data-testid={`course-image-${c.slug}`}>
        <div className="zoom-frame aspect-[4/5] mb-6">
          <img src={c.image_url} alt={title} loading="lazy" />
        </div>
      </Link>
      <Link
        to={`/cursos/${urlSlug}`}
        data-testid={`course-link-${c.slug}`}
        className="font-serif text-3xl tracking-tight mb-3 hover:text-[#8B6D5E] transition-colors"
      >
        {title}
      </Link>
      <p className="text-sm leading-relaxed text-[#A8A8A8] mb-6">{desc}</p>

      <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm border-t border-[#2a2a2a] pt-4">
        <div>
          <dt className="overline text-[#8B6D5E] mb-1">{tr(t.courses.duration, lang)}</dt>
          <dd>{c.duration}</dd>
        </div>
        <div>
          <dt className="overline text-[#8B6D5E] mb-1">{tr(t.courses.next_date, lang)}</dt>
          <dd>{fmtDate(c.next_date)}</dd>
        </div>
        <div>
          <dt className="overline text-[#8B6D5E] mb-1">{tr(t.courses.spots, lang)}</dt>
          <dd>{c.spots}</dd>
        </div>
        <div>
          <dt className="overline text-[#8B6D5E] mb-1">{tr(t.courses.price, lang)}</dt>
          <dd>${c.price.toLocaleString("es-MX")} MXN</dd>
        </div>
      </dl>

      <div className="mt-8 grid grid-cols-2 gap-3 border-t border-[#2a2a2a] pt-4">
        <Link
          to={`/cursos/${urlSlug}`}
          data-testid={`course-detail-${c.slug}`}
          className="inline-flex items-center gap-2 overline text-[#FAFAFA] hover:text-[#8B6D5E] transition-colors"
        >
          Ver detalle <ArrowRight size={14} />
        </Link>
        <button
          onClick={() => onEnroll(c)}
          data-testid={`course-enroll-${c.slug}`}
          className="group inline-flex items-center gap-2 overline text-[#8B6D5E] hover:text-[#FAFAFA] transition-colors justify-end"
        >
          {tr(t.courses.enroll, lang)}
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </article>
  );
}
